import { z } from 'zod';

import {
  ComparisonPeriodSchema,
  DimensionBaseItemSchema,
  DimensionItemSchema,
  FormattedCurrencySchema,
  MetricItemSchema,
  MetricScaleSchema,
} from './common.js';
import { FilterSchema } from './filters.js';

const TextWidgetFormatSchema = z.object({
  underline: z.boolean().optional(),
  bold: z.boolean().optional(),
  italic: z.boolean().optional(),
  strikethrough: z.boolean().optional(),
});

const TextWidgetSettingsSchema = z.object({
  size: z.enum(['h1', 'h2', 'h3', 'h4']).optional(),
  alignment: z.enum(['left', 'center', 'right']).optional(),
  format: TextWidgetFormatSchema.optional(),
});

const CombinedSettingsSchema = z.object({
  barMetricsIds: z.array(z.string()).optional(),
  lineMetricsIds: z.array(z.string()).optional(),
});

const ComparisonModeSchema = z.enum([
  'none',
  'previous-bucket',
  'previous-period',
  'previous-month',
  'previous-year',
]);

const LegacyComparisonSettingsSchema = z.object({
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  periodId: ComparisonPeriodSchema.optional(),
});

const NewComparisonSettingsSchema = z.object({
  mode: ComparisonModeSchema,
  depth: z.number().optional(),
});

const ComparisonSettingsSchema = z.union([
  NewComparisonSettingsSchema,
  LegacyComparisonSettingsSchema,
]);

const WidgetUICustomizationsSchema = z.object({
  showValues: z.boolean().optional(),
  showPoints: z.boolean().optional(),
  showAbsoluteValues: z.boolean().optional(),
  showPercentageValues: z.boolean().optional(),
  showPercentageValuesBars: z.boolean().optional(),
  showLegend: z.boolean().optional(),
});

export const WidgetColorsSchema = z.object({
  id: z.string(),
  color: z.string(),
  overrideGlobal: z.boolean().optional(),
});

export const GlobalColorSchema = WidgetColorsSchema.omit({
  overrideGlobal: true,
});

const MetricTransformationStateSchema = z.object({
  state: z.enum(['none', 'percentOfParent', 'percentOfTotal']),
  showBoth: z.boolean(),
});

export const MetricTransformationStatesSchema = z.record(
  z.string(),
  MetricTransformationStateSchema,
);

export const WidgetSettingsSchema = z
  .object({
    multiChartLayers: z.number().optional(),
    missingValuesFill: z
      .enum(['null', 'none_fill', 'previous_value_fill'])
      .nullable()
      .optional(),
    combinedSettings: CombinedSettingsSchema.optional(),
    comparison: ComparisonSettingsSchema.optional(),
    textSettings: TextWidgetSettingsSchema.optional(),
    customizations: WidgetUICustomizationsSchema.optional(),
    colors: z.array(WidgetColorsSchema).optional(),
    columnTransform: MetricTransformationStatesSchema.optional(),
    changedColumnSizes: z.record(z.string(), z.number()).optional(),
    metricScale: MetricScaleSchema.optional(),
    enableIconInTextWidget: z.boolean().optional(),
  })
  .catchall(z.unknown());

export const WidgetDimensionsSchema = z.object({
  xAxisDimension: DimensionItemSchema.nullable().optional(),
  dimension: DimensionItemSchema.nullable().optional(),
  secondLayerDimension: DimensionItemSchema.nullable().optional(),
  thirdLayerDimension: DimensionItemSchema.nullable().optional(),
  appliedList: z.array(DimensionBaseItemSchema).optional(),
  rowGroups: z.array(DimensionItemSchema).optional(),
  columnBreakdown: z.array(DimensionItemSchema).optional(),
});

export const WidgetRectangleSchema = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
  minW: z.number().optional(),
  minH: z.number().optional(),
});

export const WidgetSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum([
    'overall',
    'line_chart',
    'bar_chart',
    'combined_chart',
    'pie_chart',
    'text',
    'stacked_bar_chart',
    'stacked_combined_chart',
    'horizontal_bar_chart',
    'multilayer_pie_chart',
    'pivot_table',
    'pivot_table_v2',
  ]),
  rect: WidgetRectangleSchema,
  metrics: z.array(MetricItemSchema),
  settings: WidgetSettingsSchema.optional(),
  filters: z.array(FilterSchema),
  currency: FormattedCurrencySchema.nullish(),
  currencies: z.array(FormattedCurrencySchema).optional(),
  dimensions: WidgetDimensionsSchema,
  text: z.string().optional(),
  fileName: z.string().optional(),
});
